import { colorUtils } from '../utils';

import type { DocumentServicesObject } from '@wix/document-services-types';
import type { TPAMessageCallback } from '../types/tpaMessage';
import type { TpaStyleService } from './tpaStyleService';

const { getAlphaFromRgba, hexToRGBA, isHexColor, isHexaColor } = colorUtils;

export interface ResponseColorData {
  color: string;
  theme: string | undefined;
  cssVariableTheme: string;
  isHover: boolean;
}

export const createColorPickerStyleService = (
  documentServicesAPI: DocumentServicesObject,
) => {
  const onStyleChange = (
    compId: string,
    wixParam: string,
    colorName: string,
    callback: TPAMessageCallback,
    setStyleParamsFn: TpaStyleService['setStyleParam'],
  ) => {
    const compStyle = documentServicesAPI.tpa.style.get(compId) || {};
    const colorStyle = compStyle.style?.colors[wixParam] || {};
    const alpha = getAlphaFromRgba(colorStyle.value);
    const opacity = alpha ? +alpha : 1;

    const styleParams = {
      key: wixParam,
      param: {
        value: {
          opacity,
        },
      },
      type: 'color' as const,
    };

    if (isHexColor(colorName) || isHexaColor(colorName)) {
      const colorAsRGBA = hexToRGBA(colorName, opacity);
      Object.assign(styleParams.param.value, {
        rgba: colorAsRGBA,
      });
    } else {
      Object.assign(styleParams.param.value, { color: { name: colorName } });
    }

    const compRef = documentServicesAPI.components.get.byId(compId);

    if (!compRef) {
      throw new Error(`No compRef found for compId: ${compId}`);
    }

    const styleChangeCallback = () => {
      const updatedStyle = documentServicesAPI.tpa.style.get(compId);

      callback({
        colorParam: updatedStyle.style.colors[wixParam],
      });
    };

    setStyleParamsFn(compRef, styleParams, styleChangeCallback, false);
  };

  const onStyleChangeWithoutWixParam = (
    colorData: ResponseColorData,
    callback: TPAMessageCallback,
  ) => {
    const newColorName = colorData.theme || colorData.color;
    const colorParam: Record<string, any> = {};
    const key = colorData.theme ? 'themeName' : 'value';
    colorParam[key] = newColorName;

    callback({ colorParam });
  };

  return { onStyleChange, onStyleChangeWithoutWixParam };
};
